import React, { useState } from "react";
import { CancelacionPreliminarGuarderia, cancelarGuarderia, QUERY_OBTENER_CANCELACION_PRELIMINAR_GUARDERIA, SolicitudCancelacionGuarderia } from "../../../services/cancelacionesServicios";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { InfoRow } from "../../organisms/InfoRow";
import CurrencyFormat from "react-currency-format";
import { AgendamientoGuarderiaResumido } from "../../../services/agendamientosGuarderiaServicios";
import styled from "styled-components";
import { currencyFormat, parseCurrency } from "../../../utils/numberUtils";
import FechasCancelacion from "./FechasCancelacion";
import FechasCancelacionTranpIda from "./FechasCancelacionTranpIda";
import FechasCancelacionTranpVuelta from "./FechasCancelacionTranpVuelta";
import { Model, useCancelacionPreliminar, ValidacionCancelacion, validarCancelacion } from "./cancelacionPreliminar";
import ServerErrorModal from "../../organisms/ServerErrorModal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { RUTA_RESERVAS_GUARDERIA } from "../../../services/rutas";
import AlertModal from "../../organisms/AlertModal";

type CancelacionPreliminarProps = {
    cancelacionPreliminar: CancelacionPreliminarGuarderia,
    agendamiento: AgendamientoGuarderiaResumido
};

function CancelacionPreliminar({ cancelacionPreliminar, agendamiento }: CancelacionPreliminarProps) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [validacion, setValidacion] = useState<ValidacionCancelacion>({ valido: true, razon: '' });

    const {
        model,
        actualizarTotalNotaCredito,
        actualizarTotaDevolucion,
        seleccionarMetodoPagoDevolucion
    } = useCancelacionPreliminar(cancelacionPreliminar);

    const {
        error: errorMutacionCancelar,
        isLoading: cancelacionIsLoading,
        mutate,
    } = useMutation(cancelarGuarderia, {
        onSuccess: () => {
            queryClient.invalidateQueries([QUERY_OBTENER_CANCELACION_PRELIMINAR_GUARDERIA]);
            navigate(`${RUTA_RESERVAS_GUARDERIA}/${agendamiento.id}`);
        },
    });

    const puedeFinalizar = puedeCancelar(model);

    const onTotalNotaCreditoChange = (val: React.ChangeEvent<HTMLInputElement>) => {
        const nuevoTotal = parseCurrency(val.target.value, "$", ",");
        actualizarTotalNotaCredito(nuevoTotal);
    };

    const onTotalDevolucionChange = (val: React.ChangeEvent<HTMLInputElement>) => {
        const nuevoTotal = parseCurrency(val.target.value, "$", ",");
        actualizarTotaDevolucion(nuevoTotal);
    };

    const onCancelar = () => {
        setValidacion({ valido: true, razon: '' });
        const respValidacion = validarCancelacion(model);
        setValidacion(respValidacion);

        if(!respValidacion.valido){
            return;
        }
        const solicitud: SolicitudCancelacionGuarderia = {
            idAgendamiento: agendamiento.id,
            notaCredito: model.totalNotaCredito > 0 ? { valor: model.totalNotaCredito } : undefined,
            devolucion: model.totalDevolucion > 0 ? { valor: model.totalDevolucion, metodo: model.metodoPagoDevolucion!! } : undefined,
        }
        mutate(solicitud);
    }

    const onCloseAlertModalValidacion = () => {
        setValidacion({valido: true, razon: ""});
    }

    if (errorMutacionCancelar) {
        return <ServerErrorModal />;
    }

    return (
        <>
            {!validacion.valido && <AlertModal title="Datos inválidos" message={validacion.razon} onClose={onCloseAlertModalValidacion} />}
            <div className="p-3">
                <InfoRow label="Mascota" value={agendamiento.mascota} />
                <section className="mb-4 mt-3">
                    <SectionTitle className="mb-3">
                        Tarifas y planes
                    </SectionTitle>
                    {agendamiento.reservaGuarderia.plan && (
                        <div className="mb-4">
                            <InfoRow label="Plan" value={agendamiento.reservaGuarderia.plan!} />
                            <InfoRow
                                label="Valor final plan"
                                value={currencyFormat(agendamiento.reservaGuarderia.precioFinalPlan!)}
                            />
                        </div>
                    )}

                    {agendamiento.reservaGuarderia.servicioDiaIndividual && (
                        <div className="mb-4">
                            <InfoRow
                                label="Días individuales"
                                value={`${agendamiento.reservaGuarderia.diasIndividuales!}`}
                            />
                            <InfoRow
                                label="Tarifa día individual"
                                value={agendamiento.reservaGuarderia.servicioDiaIndividual}
                            />
                            <InfoRow
                                label="Valor final día individual"
                                value={currencyFormat(agendamiento.reservaGuarderia.precioFinalDiaIndividual!)}
                            />
                        </div>
                    )}
                    {agendamiento.reservaTransporteIda && (
                        <div className="mb-2">
                            <InfoRow
                                label="Viajes Transporte Ida"
                                value={`${agendamiento.reservaTransporteIda.fechasProgramadas.length}`}
                            />
                            <InfoRow
                                label="Tarifa Transporte Ida"
                                value={currencyFormat(agendamiento.reservaTransporteIda.precioFinalTarifa)}
                            />
                        </div>
                    )}
                    {agendamiento.reservaTransporteVuelta && (
                        <div className="mb-2">
                            <InfoRow
                                label="Viajes Transporte Vuelta"
                                value={`${agendamiento.reservaTransporteVuelta.fechasProgramadas.length}`}
                            />
                            <InfoRow
                                label="Tarifa Transporte Vuelta"
                                value={currencyFormat(agendamiento.reservaTransporteVuelta.precioFinalTarifa)}
                            />
                        </div>
                    )}
                    {agendamiento.reservaTransporteIdaVuelta && (
                        <div className="mb-2">
                            <InfoRow
                                label="Viajes Transporte Ida y Vuelta"
                                value={`${agendamiento.reservaTransporteIdaVuelta.fechasProgramadas.length}`}
                            />
                            <InfoRow
                                label="Tarifa Transporte Ida y Vuelta"
                                value={currencyFormat(agendamiento.reservaTransporteIdaVuelta.precioFinalTarifa)}
                            />
                        </div>
                    )}
                </section>
                <section className="mb-4">
                    <SectionTitle className="mb-3">
                        Fechas Programadas
                    </SectionTitle>
                    <FechasCancelacion cancelacionPreliminar={cancelacionPreliminar} />
                </section>
                {
                    model.fechasProgramadasTransporteIda.length > 0 && (
                        <section className="mb-4">
                            <SectionTitle className="mb-3">
                                Fechas Transporte Ida
                            </SectionTitle>
                            <FechasCancelacionTranpIda cancelacionPreliminar={cancelacionPreliminar} />
                        </section>
                    )
                }
                {
                    model.fechasProgramadasTransporteVuelta.length > 0 && (
                        <section className="mb-4">
                            <SectionTitle className="mb-3">
                                Fechas Transporte Vuelta
                            </SectionTitle>
                            <FechasCancelacionTranpVuelta cancelacionPreliminar={cancelacionPreliminar} />
                        </section>
                    )
                }
                <section className="mb-4">
                    <SectionTitle className="mb-3">Totales y Devoluciones</SectionTitle>
                    <Row className="mb-2">
                        <LabelCol className="col-5">
                            Total Reserva Guardería
                        </LabelCol>
                        <Col></Col>
                        <Col className="text-right">
                            {currencyFormat(model.totalReserva)}
                        </Col>
                    </Row>
                    {
                        model.totalReservaTransporte > 0 && (
                            <Row className="mb-2">
                                <LabelCol className="col-5">
                                    Total Transporte
                                </LabelCol>
                                <Col></Col>
                                <Col className="text-right">
                                    {currencyFormat(model.totalReservaTransporte)}
                                </Col>
                            </Row>
                        )
                    }
                    {
                        model.descuento > 0 && (
                            <Row className="mb-2">
                                <LabelCol className="col-5">
                                    Descuento
                                </LabelCol>
                                <Col></Col>
                                <Col className="text-right">
                                    {currencyFormat(model.descuento)}
                                </Col>
                            </Row>
                        )
                    }
                    <Row className="mb-2">
                        <LabelCol className="col-5">
                            Total Agendamiento
                        </LabelCol>
                        <Col></Col>
                        <Col className="text-right">
                            {currencyFormat(model.totalAgendamiento)}
                        </Col>
                    </Row>

                    {
                        model.totalPago > 0 && (
                            <Row className="mb-2">
                                <LabelCol className="col-5">
                                    Total Pago
                                </LabelCol>
                                <Col></Col>
                                <Col className="text-right">
                                    {currencyFormat(model.totalPago)}
                                </Col>
                            </Row>
                        )
                    }
                    <Row className="mb-2">
                        <LabelCol className="col-5">
                            Total Nota Crédito
                        </LabelCol>
                        <Col></Col>
                        <Col className="text-right">
                            <CurrencyFormat
                                name="totalNotaCredito"
                                value={model.totalNotaCredito}
                                thousandSeparator={","}
                                onChange={onTotalNotaCreditoChange}
                                prefix={"$"}
                                decimalSeparator={"."}
                                max={model.totalAgendamiento}
                                min={0}
                            />
                        </Col>
                    </Row>

                    <Row className="mb-2">
                        <LabelCol className="col-5">
                            Total Devolución
                        </LabelCol>
                        <Col></Col>
                        <Col className="text-right">
                            <CurrencyFormat
                                name="totalDevolucion"
                                value={model.totalDevolucion}
                                thousandSeparator={","}
                                onChange={onTotalDevolucionChange}
                                prefix={"$"}
                                decimalSeparator={"."}
                                max={model.totalPago}
                                min={0}
                            />
                        </Col>
                    </Row>
                    {
                        model.totalDevolucion > 0 && (
                            <Row className="mb-2">
                                <LabelCol className="col-5">
                                    Método Devolución
                                </LabelCol>
                                <Col></Col>
                                <Col className="text-right">
                                    <Form>
                                        <Form.Check
                                            type="radio"
                                            id="metodo_efectivo"
                                            label="Efectivo"
                                            name="metodo"
                                            onChange={() => seleccionarMetodoPagoDevolucion("EFECTIVO")}
                                            value={"EFECTIVO"}
                                            required
                                        />

                                        <Form.Check
                                            type="radio"
                                            id="metodo_nequi"
                                            label="Nequi"
                                            name="metodo"
                                            onChange={() => seleccionarMetodoPagoDevolucion("NEQUI")}
                                            value={"NEQUI"}
                                            required
                                        />

                                        <Form.Check
                                            type="radio"
                                            id="metodo_daviplata"
                                            label="Daviplata"
                                            name="metodo"
                                            onChange={() => seleccionarMetodoPagoDevolucion("DAVIPLATA")}
                                            required
                                            value={"DAVIPLATA"}
                                        />

                                        <Form.Check
                                            type="radio"
                                            id="metodo_transferencia"
                                            label="Transferencia"
                                            name="metodo"
                                            onChange={() => seleccionarMetodoPagoDevolucion("TRANSFERENCIA")}
                                            required
                                            value={"TRANSFERENCIA"}
                                        />

                                        <Form.Check
                                            type="radio"
                                            id="metodo_transferencia"
                                            label="Tarjeta de Crédito"
                                            name="metodo"
                                            onChange={() => seleccionarMetodoPagoDevolucion("TARJETA_CREDITO")}
                                            required
                                            value={"TARJETA_CREDITO"}
                                        />
                                    </Form>
                                </Col>
                            </Row>
                        )
                    }
                    <Row className="mb-2">
                        <LabelCol className="col-5">
                            Nuevo Pendiente Venta
                        </LabelCol>
                        <Col></Col>
                        <Col className="text-right">
                            {currencyFormat(model.nuevoPendiente)}
                        </Col>
                    </Row>
                </section>
            </div>
            <Row>
                <Col className="gap-2 d-flex justify-content-center">
                    <Button
                        disabled={!puedeFinalizar}
                        onClick={onCancelar}
                        variant="danger"
                    >
                        {cancelacionIsLoading ? <Spinner animation="border" size="sm" /> : "Confirmar"}
                    </Button>
                    <Button href={`/reservas/guarderia/${model.idAgendamiento}`}>Ver Agendamiento</Button>
                </Col>
            </Row>
        </>
    );
}

const puedeCancelar = (model: Model): Boolean => {
    if (model.totalDevolucion === 0 && model.totalNotaCredito === 0) {
        return false;
    }
    if (model.totalDevolucion > 0 && !model.metodoPagoDevolucion) {
        return false;
    }
    if (model.totalDevolucion === 0 && model.metodoPagoDevolucion !== undefined) {
        return false;
    }
    return true;
}

const LabelCol = styled(Col)`
  font-weight: 500;
`;

const SectionTitle = styled("p")`
  font-weight: 700;
`;

export default CancelacionPreliminar;